import React, { useEffect, useMemo, useRef } from 'react';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import config from '../config';
import { Events, EventName, IdentityProperties } from '../analytics';
import { useDebouncedCallback } from '@mantine/hooks';
import { useLocation, useNavigate } from 'react-router-dom';

export function AnalyticsProvider({ children }: React.PropsWithChildren) {
  return (
    <PostHogProvider
      apiKey={config.POSTHOG.KEY}
      options={{
        autocapture: false,
        debug: config.POSTHOG.DEBUG,
        advanced_disable_feature_flags_on_first_load: true,
        session_recording: {
          maskAllInputs: false,
          maskInputOptions: {
            password: true,
          },
        },
        api_host: config.POSTHOG.API_HOST,
        ui_host: config.POSTHOG.UI_HOST,
      }}
    >
      {children}
    </PostHogProvider>
  );
}

export default function useAnalytics() {
  const posthog = usePostHog();
  const location = useLocation();
  const navigate = useNavigate();

  const pageview = useDebouncedCallback(() => {
    posthog.capture('$pageview');

    const params = new URLSearchParams(location.search);
    if (params.has('ref')) {
      params.delete('ref');
      navigate(location.pathname + '?' + params.toString(), { replace: true });
    }
  }, 500);

  const hasIdentifiedRef = useRef(false);
  const hasSetIdentityPropertiesRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(
      () => {
        posthog.reloadFeatureFlags();
      },
      1000 * 60 * 10,
    );

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [posthog]);

  useEffect(() => {
    posthog.register({
      product: 'WEB',
    });
  }, []);

  const cachedValue = useMemo(
    () => ({
      pageview,
      capture: function capture<E extends EventName>(event: E, properties?: Events[E]) {
        posthog.capture(event, {
          ...properties,
          product: 'WEB',
        });
      },

      identify: (distinctId: string, properties?: IdentityProperties) => {
        if (hasIdentifiedRef.current) {
          return;
        }
        posthog.identify(distinctId, properties);
        hasIdentifiedRef.current = true;
      },

      setIdentityProperties: (properties: IdentityProperties) => {
        if (hasSetIdentityPropertiesRef.current) {
          return;
        }
        posthog.setPersonProperties(properties);
        hasSetIdentityPropertiesRef.current = true;
      },

      reset: () => {
        posthog.reset();
        hasIdentifiedRef.current = false;
        hasSetIdentityPropertiesRef.current = false;
      },
    }),
    [posthog, pageview],
  );
  return cachedValue;
}
